import { HttpClient } from "@angular/common/http";
import { isPlatformBrowser } from "@angular/common";
import { Injectable, PLATFORM_ID, REQUEST, inject } from "@angular/core";
import { EMPTY, catchError } from "rxjs";
import {environment} from "../../../environments/environment";
import {
  Action,
  Touchpoint,
  UserAction,
  UserImmersiveUseAction,
  UserLikeAction,
  UserLoginAction, UserLoginKeycloakAction,
  UserMapUseAction,
  UserShareAction,
  UserSignupAction,
  UserVisitAction,
  UserXploreVisitAction
} from "../../shared/object/xplorer-analytics-devourer-parsed-classes";
import {TokenStorageService} from "../auth/token-storage.service";
import {TerritoryService} from "../geolocation/territory.service";

@Injectable({
  providedIn: 'root',
})
export class AnalyticsService {

  private readonly http: HttpClient = inject(HttpClient);
  private readonly tokenStorageService: TokenStorageService = inject(TokenStorageService);
  private readonly territoryService: TerritoryService = inject(TerritoryService);
  private readonly platformId = inject(PLATFORM_ID);
  private readonly isBrowser = isPlatformBrowser(this.platformId);
  private readonly request = inject(REQUEST, { optional: true }) as any;


  addUserAction(userId: string, action: Action) {

  }

  saveLikeAction(xploreId: number) {
    if (!this.shouldTrack()) {
      return;
    }

    const likeAction: UserLikeAction = {
      ...this.getBasicAction("LIKE"),
      xploreId: xploreId
    };

    this.trackAction('like', likeAction);
  }

  addShareAction(xploreId: number) {
    if (!this.shouldTrack()) {
      return;
    }

    const shareAction: UserShareAction = {
      ...this.getBasicAction("SHARE"),
      xploreId: xploreId
    };

    this.trackAction('share', shareAction);
  }


  addXploreVisitAction(xploreId: number) {
    if (!this.shouldTrack()) {
      return;
    }

    const xploreVisitAction: UserXploreVisitAction = {
      ...this.getBasicAction("XPLORE_VISIT"),
      xploreId: xploreId,
      spentTime: 0
    };

    this.trackAction('xplore-visit', xploreVisitAction);
  }

  addVisitAction() {
    if (!this.shouldTrack()) {
      return;
    }

    const visitAction: UserVisitAction = {
      ...this.getBasicAction("VISIT"),
      spentTime: 0
    };

    this.trackAction('visit', visitAction);
  }

  addImmersiveUseAction(xploreId: number) {
    if (!this.shouldTrack()) {
      return;
    }

    const userImmersiveUseAction: UserImmersiveUseAction = {
      ...this.getBasicAction("IMMERSIVE_USE"),
      xploreId: xploreId,
      spentTime: 0
    };

    this.trackAction('immersive-use', userImmersiveUseAction);
  }

  addMapUseAction() {
    if (!this.shouldTrack()) {
      return;
    }

    const mapUseAction: UserMapUseAction = {
      ...this.getBasicAction("MAP_USE")
    };

    this.trackAction('map-use', mapUseAction);
  }

  addLoginAction() {
    if (!this.shouldTrack()) {
      return;
    }

    const loginAction: UserLoginAction = {
      ...this.getBasicAction("LOGIN")
    };

    this.trackAction('login', loginAction);
  }

  addSignupAction(email: string) {
    if (!this.shouldTrack()) {
      return;
    }

    const signupAction: UserSignupAction = {
      ...this.getBasicAction("SIGNUP"),
      userEmail: email,
      userId: -1
    };

    this.trackAction('signup', signupAction);
  }

  addLoginKeycloakAction() {
    if (!this.shouldTrack()) {
      return;
    }

    const loginKeycloakAction: UserLoginKeycloakAction = {
      ...this.getBasicAction("LOGIN_KEYCLOAK")
    };

    this.trackAction('login-keycloak', loginKeycloakAction);
  }


  private retrieveUserId() {
    let userId: number;
    let user = this.tokenStorageService.getUser();
    if (user) {
      userId = user.userId;
    } else {
      userId = -1;
    }
    return userId;
  }

  private retrieveUserEmail() {
    let userEmail: string;
    let user = this.tokenStorageService.getUser();
    if (user) {
      userEmail = user.email;
    } else {
      userEmail = "";
    }
    return userEmail;
  }

  private getBasicAction(actionTye: Action) {

    const shareAction: UserAction = {
      id: 0,
      userId: this.retrieveUserId(),
      userEmail: this.retrieveUserEmail(),
      actionTime: new Date(),
      touchpoint: this.getTouchpoint(),
      territoryCode: this.getTerritoryCode(),
      userActionType: actionTye
    }

    return shareAction;
  }

  private trackAction(endpoint: string, action: UserAction): void {
    this.http.post<number>(`${environment.ANALYTICS_DEVOURER_API_URL}/user-actions/${endpoint}`, action)
      .pipe(
        catchError(() => EMPTY)
      )
      .subscribe();
  }

  private getTouchpoint(): Touchpoint {
    if (this.isBrowser) {
      if (window.matchMedia('(display-mode: standalone)').matches) {
        return "PWA";
      }

      if (window.matchMedia("(max-width: 767px)").matches) {
        return "WEB_MOBILE";
      }

      return "WEB_DESKTOP";
    }

    const userAgent = this.getRequestHeader('user-agent')?.toLowerCase() ?? '';
    if (/(iphone|ipad|ipod|android|mobile|blackberry|webos)/.test(userAgent)) {
      return "WEB_MOBILE";
    }

    return "WEB_DESKTOP";
  }

  private shouldTrack(): boolean {
    if (this.isBrowser) {
      return true;
    }

    // During prerender/build there is no incoming HTTP request context.
    return !!this.getRequestUrl();
  }

  private getTerritoryCode(): string {
    if (this.isBrowser) {
      return this.territoryService.getTerritoryCodeFromUrl();
    }

    const fallbackTerritory = this.territoryService.getTerritoryCodeFromUrl();
    const host = this.getRequestHeader('host');
    return TerritoryService.resolveTerritoryCodeFromHostname(host ?? '', fallbackTerritory);
  }

  private getRequestUrl(): string | null {
    const requestUrl = this.request?.url;
    return typeof requestUrl === 'string' && requestUrl.length > 0 ? requestUrl : null;
  }

  private getRequestHeader(name: string): string | null {
    const lowerName = name.toLowerCase();
    const headers = this.request?.headers;

    if (!headers) {
      return null;
    }

    if (typeof headers.get === 'function') {
      return headers.get(name) ?? headers.get(lowerName);
    }

    const headerValue = headers[lowerName] ?? headers[name];
    if (Array.isArray(headerValue)) {
      return headerValue[0] ?? null;
    }
    return typeof headerValue === 'string' ? headerValue : null;
  }
}
